<p-toast></p-toast>
<p-confirmDialog></p-confirmDialog>

<app-page-shell>
    <app-page-hero
        eyebrow="Company Administration"
        title="Users & Roles"
        [subtitle]="'Manage company staff roster, portal access assignment, and custom roles' + (companyName ? ' for ' + companyName : '') + '.'">
        <div nav class="flex gap-2">
            <a pButton routerLink="/company" label="Company Setup" icon="pi pi-building" [outlined]="true" severity="secondary"></a>
            <button pButton label="Users & Roles" icon="pi pi-users" class="p-button-primary"></button>
        </div>
        <div actions class="flex gap-2">
            <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" [outlined]="true" (onClick)="refreshAll()"></p-button>
            <p-button *ngIf="activeTabIndex === 0" label="New Employee" icon="pi pi-plus" (onClick)="createNewEmployee()"></p-button>
            <p-button *ngIf="activeTabIndex === 1" label="Create Custom Role" icon="pi pi-plus" severity="success" (onClick)="openCreateCustomRole()"></p-button>
        </div>
    </app-page-hero>

    <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

    <app-page-data-panel
        title="Users & Roles Workspace"
        subtitle="Manage company employees and configure tailored custom roles.">

        <p-tabs [value]="activeTabIndex" (valueChange)="onTabChange($event)">
            <p-tablist>
                <p-tab [value]="0">
                    <span class="tab-label">
                        <i class="pi pi-users"></i> Employees ({{ customers.length }})
                    </span>
                </p-tab>
                <p-tab [value]="1">
                    <span class="tab-label">
                        <i class="pi pi-shield"></i> Roles & Permissions ({{ roles.length }})
                    </span>
                </p-tab>
            </p-tablist>

            <p-tabpanels>
                <!-- TAB 0: EMPLOYEES -->
                <p-tabpanel [value]="0">
                    <div class="table-toolbar">
                        <span class="search-field">
                            <i class="pi pi-search"></i>
                            <input pInputText [(ngModel)]="employeeSearch" placeholder="Search employees by name, email, phone..." />
                        </span>
                        <button pButton type="button" label="New Employee" icon="pi pi-plus" (click)="createNewEmployee()"></button>
                    </div>

                    <p-table [value]="visibleCustomers" dataKey="id" styleClass="p-datatable-sm" [loading]="isLoadingEmployees" [tableStyle]="{ 'min-width': '62rem' }">
                        <ng-template pTemplate="header">
                            <tr>
                                <th>ID</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Phone Number</th>
                                <th>Assigned Roles</th>
                                <th class="text-right">Actions</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-customer>
                            <tr>
                                <td>{{ customer.id }}</td>
                                <td>
                                    <div class="identity-cell">
                                        <p-avatar [image]="customer.imageUrl || 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png'" shape="circle"></p-avatar>
                                        <strong>{{ customer.name }}</strong>
                                    </div>
                                </td>
                                <td>{{ customer.emailAddress?.includes('@abcxyz.com') ? '-' : customer.emailAddress }}</td>
                                <td>{{ customer.phoneNumber || 'N/A' }}</td>
                                <td>
                                    <div class="chip-list">
                                        <p-tag *ngFor="let role of customer.roles"
                                               [value]="role.roleName"
                                               [severity]="role.custom ? 'success' : 'info'">
                                        </p-tag>
                                        <span *ngIf="!customer.roles || customer.roles.length === 0" class="text-secondary italic">No roles assigned</span>
                                    </div>
                                </td>
                                <td class="text-right">
                                    <button pButton pTooltip="Send Email" type="button" icon="pi pi-envelope"
                                            (click)="sendEmail(customer.emailAddress)"
                                            [disabled]="!customer.emailAddress || customer.emailAddress.includes('@abcxyz.com')"
                                            class="p-button-rounded p-button-text p-button-secondary"></button>
                                    <button pButton pTooltip="Call" type="button" icon="pi pi-phone"
                                            (click)="makePhoneCall(customer.phoneNumber)"
                                            [disabled]="!customer.phoneNumber || customer.phoneNumber === 'N/A'"
                                            class="p-button-rounded p-button-text p-button-success"></button>
                                    <button pButton pTooltip="Manage Roles" type="button" icon="pi pi-users"
                                            (click)="manageRolesForEmployee(customer)"
                                            class="p-button-rounded p-button-text p-button-help"></button>
                                    <button pButton pTooltip="Edit" type="button" icon="pi pi-pencil"
                                            (click)="editEmployee(customer)"
                                            class="p-button-rounded p-button-text p-button-secondary"></button>
                                </td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr><td colspan="6" class="text-center p-4">No employees found.</td></tr>
                        </ng-template>
                    </p-table>
                </p-tabpanel>

                <!-- TAB 1: ROLES & PERMISSIONS -->
                <p-tabpanel [value]="1">
                    <div class="table-toolbar">
                        <div class="flex-row gap-2">
                            <span class="search-field">
                                <i class="pi pi-search"></i>
                                <input pInputText [(ngModel)]="roleSearch" placeholder="Search roles by name or description..." />
                            </span>
                            <div class="filter-pills">
                                <button pButton type="button" label="All ({{ roles.length }})"
                                        [outlined]="roleFilter !== 'ALL'" size="small" (click)="roleFilter = 'ALL'"></button>
                                <button pButton type="button" label="Custom Roles ({{ customRolesCount }})"
                                        [outlined]="roleFilter !== 'CUSTOM'" size="small" severity="success" (click)="roleFilter = 'CUSTOM'"></button>
                                <button pButton type="button" label="Standard Templates ({{ standardRolesCount }})"
                                        [outlined]="roleFilter !== 'STANDARD'" size="small" severity="info" (click)="roleFilter = 'STANDARD'"></button>
                            </div>
                        </div>
                        <button pButton type="button" label="Create Custom Role" icon="pi pi-plus" (click)="openCreateCustomRole()"></button>
                    </div>

                    <p-table [value]="filteredRoles" dataKey="roleId" styleClass="p-datatable-sm" [loading]="isLoadingRoles" [tableStyle]="{ 'min-width': '64rem' }">
                        <ng-template pTemplate="header">
                            <tr>
                                <th>Role Name</th>
                                <th>Type</th>
                                <th>Stitched Standard Roles</th>
                                <th>Permissions</th>
                                <th>Assigned Users</th>
                                <th class="text-right">Actions</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-role>
                            <tr>
                                <td>
                                    <div class="role-identity">
                                        <strong>{{ role.roleName }}</strong>
                                        <span class="role-desc text-secondary" *ngIf="role.description">{{ role.description }}</span>
                                    </div>
                                </td>
                                <td>
                                    <p-tag *ngIf="role.custom" value="Custom" severity="success" icon="pi pi-check"></p-tag>
                                    <p-tag *ngIf="!role.custom" value="Standard (Template)" severity="info" icon="pi pi-lock" pTooltip="System standard role. Immutable."></p-tag>
                                </td>
                                <td>
                                    <div class="chip-list" *ngIf="role.custom">
                                        <p-tag *ngFor="let stitched of role.stitchedRoleNames"
                                               [value]="stitched"
                                               icon="pi pi-link"
                                               severity="secondary">
                                        </p-tag>
                                        <span *ngIf="role.stitchedRoleNames.length === 0" class="text-secondary italic">Handpicked only</span>
                                    </div>
                                    <span *ngIf="!role.custom" class="text-secondary italic">Standard Baseline</span>
                                </td>
                                <td>
                                    <p-tag [value]="role.permissionCount + ' permissions'" [severity]="role.custom ? 'warn' : 'info'"></p-tag>
                                </td>
                                <td>
                                    <span class="user-count-badge">
                                        <i class="pi pi-user text-secondary"></i> {{ role.assignedUsersCount }}
                                    </span>
                                </td>
                                <td class="text-right">
                                    <!-- Custom Role Actions -->
                                    <ng-container *ngIf="role.custom">
                                        <button pButton pTooltip="Edit Role" type="button" icon="pi pi-pencil"
                                                (click)="editCustomRole(role)"
                                                class="p-button-rounded p-button-text p-button-secondary"></button>
                                        <button pButton pTooltip="Delete Role" type="button" icon="pi pi-trash"
                                                (click)="deleteCustomRole(role)"
                                                class="p-button-rounded p-button-text p-button-danger"></button>
                                    </ng-container>

                                    <!-- Standard Role Actions (Immutable) -->
                                    <ng-container *ngIf="!role.custom">
                                        <button pButton pTooltip="View Permissions (Read-only)" type="button" icon="pi pi-eye"
                                                (click)="inspectStandardRole(role)"
                                                class="p-button-rounded p-button-text p-button-info"></button>
                                    </ng-container>
                                </td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr><td colspan="6" class="text-center p-4">No roles found matching your criteria.</td></tr>
                        </ng-template>
                    </p-table>
                </p-tabpanel>
            </p-tabpanels>
        </p-tabs>
    </app-page-data-panel>
</app-page-shell>

<!-- Dialog 1: Customer Form Modal -->
<app-customer-form
    *ngIf="showCustomerForm"
    [displayModal]="showCustomerForm"
    [dialogHeader]="customerFormHeader"
    [submitButtonLabel]="customerFormSubmitLabel"
    [customer]="selectedCustomer!"
    (modalClosed)="onCustomerFormClosed()">
</app-customer-form>

<!-- Dialog 2: Customer Roles Assignment Modal -->
<app-customer-roles
    *ngIf="showCustomerRolesDialog && selectedCustomer"
    [displayModal]="showCustomerRolesDialog"
    [dialogHeader]="'Manage Roles for ' + (selectedCustomer?.name || 'Employee')"
    [customer]="selectedCustomer!"
    (modalClosed)="onCustomerRolesDialogClosed()">
</app-customer-roles>

<!-- Dialog 3: Custom Role Builder Modal (Stitching & Handpicking) -->
<app-custom-role-builder
    *ngIf="showRoleBuilder"
    [visible]="showRoleBuilder"
    [roleId]="editingRoleId"
    (closed)="onRoleBuilderClosed($event)">
</app-custom-role-builder>

<!-- Dialog 4: Standard Role Read-Only Inspector Modal -->
<p-dialog
    [(visible)]="showStandardRoleInspector"
    [modal]="true"
    [style]="{ width: '60vw', maxWidth: '800px' }"
    header="{{ inspectedRole?.roleName || 'Standard Role' }} (Immutable Template)">
    <div *ngIf="inspectedRole" class="standard-role-inspector">
        <div class="inspector-badge-row">
            <p-tag value="Immutable System Template" icon="pi pi-lock" severity="info"></p-tag>
            <p-tag [value]="inspectedRole.permissions.length + ' permissions included'" severity="success"></p-tag>
        </div>
        <p class="inspector-desc">{{ inspectedRole.description || 'Predefined system role.' }}</p>
        <div class="inspector-permissions-list">
            <div *ngFor="let p of inspectedRole.permissions" class="inspector-perm-item">
                <strong>{{ p.permissionName }}</strong>
                <span class="text-secondary font-mono text-xs">{{ p.permissionCode }}</span>
            </div>
        </div>
    </div>
</p-dialog>

